import { zipSync } from 'fflate';
import { ANALYTICS_LOG_SCHEMA_VERSION } from '@/utils/analytics-log';
import {
  idbDeleteArchive,
  idbDeleteLogsByIds,
  idbGetArchive,
  idbGetLogsBefore,
  idbListArchives,
  idbPutArchive,
} from '@/utils/analytics-idb';

export const ANALYTICS_HOT_RETENTION_MS = 3 * 24 * 60 * 60 * 1000;
/** 冷归档 zip 本地最多再留天数（待后台上报后删除；未上报也先按此裁） */
export const ANALYTICS_ARCHIVE_KEEP_DAYS = 14;
/** 热日志硬上限（防异常刷屏） */
export const ANALYTICS_HOT_LOGS_HARD_MAX = 10000;
/** 内存里 ui.click 最多留条数（不落 IDB） */
export const ANALYTICS_CLICK_MEMORY_MAX = 100;

const DAY_MS = 24 * 60 * 60 * 1000;

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** @param {number} at */
export function analyticsDayKeyFromAt(at) {
  const d = new Date(at);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** @param {string} dayKey YYYY-MM-DD local */
export function analyticsDayStartMs(dayKey) {
  const [y, m, d] = String(dayKey)
    .split('-')
    .map((x) => Number(x));
  if (!y || !m || !d) return 0;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

/**
 * @param {Array<Record<string, unknown>>} logs
 * @param {number} [now]
 */
export function filterHotAnalyticsLogs(logs, now = Date.now()) {
  const cutoff = now - ANALYTICS_HOT_RETENTION_MS;
  const durable = [];
  const clicks = [];
  (logs || []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    if (row.key === 'ui.click') {
      clicks.push(row);
      return;
    }
    if (Number(row.at) >= cutoff) durable.push(row);
  });
  durable.sort((a, b) => Number(a.at) - Number(b.at));
  const clipped =
    durable.length > ANALYTICS_HOT_LOGS_HARD_MAX
      ? durable.slice(durable.length - ANALYTICS_HOT_LOGS_HARD_MAX)
      : durable;
  return [...clipped, ...clicks.slice(-ANALYTICS_CLICK_MEMORY_MAX)];
}

/** 仅应写入 IDB 的热日志（排除 ui.click，且在 3 天窗内） */
export function filterDurableHotLogs(logs, now = Date.now()) {
  const cutoff = now - ANALYTICS_HOT_RETENTION_MS;
  const durable = (logs || []).filter(
    (row) => row && row.key !== 'ui.click' && Number(row.at) >= cutoff
  );
  durable.sort((a, b) => Number(a.at) - Number(b.at));
  if (durable.length > ANALYTICS_HOT_LOGS_HARD_MAX) {
    return durable.slice(durable.length - ANALYTICS_HOT_LOGS_HARD_MAX);
  }
  return durable;
}

/**
 * 把已离开热窗口的日志按自然日打成 zip 写入 IDB，再删热库中对应行。
 * @param {{ deviceId?: string, userId?: string }} ctx
 */
export async function runAnalyticsDayArchive(ctx = {}) {
  const now = Date.now();
  const cutoff = now - ANALYTICS_HOT_RETENTION_MS;
  const expired = await idbGetLogsBefore(cutoff);
  if (!expired.length) {
    await pruneOldArchives(now);
    return { archivedDays: [], archivedLogs: 0 };
  }

  /** @type {Record<string, Array<Record<string, unknown>>>} */
  const byDay = {};
  expired.forEach((row) => {
    const day = analyticsDayKeyFromAt(Number(row.at) || 0);
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(row);
  });

  const archivedDays = [];
  let archivedLogs = 0;

  for (const day of Object.keys(byDay).sort()) {
    const dayLogs = byDay[day];
    const existing = await idbGetArchive(day);
    if (!existing?.blob) {
      const payload = {
        archivedAt: new Date().toISOString(),
        day,
        schemaVersion: ANALYTICS_LOG_SCHEMA_VERSION,
        deviceId: ctx.deviceId || undefined,
        userId: ctx.userId || undefined,
        logCount: dayLogs.length,
        logs: dayLogs,
      };
      const jsonText = `${JSON.stringify(payload)}\n`;
      const rawBytes = new TextEncoder().encode(jsonText);
      const jsonName = `deepsage-analytics-${day}.json`;
      const zipped = zipSync({ [jsonName]: rawBytes }, { level: 6 });
      const blob = new Blob([zipped], { type: 'application/zip' });
      await idbPutArchive({
        day,
        blob,
        byteLength: zipped.byteLength,
        logCount: dayLogs.length,
        schemaVersion: ANALYTICS_LOG_SCHEMA_VERSION,
        deviceId: ctx.deviceId || undefined,
        userId: ctx.userId || undefined,
        createdAt: Date.now(),
      });
    }
    await idbDeleteLogsByIds(dayLogs.map((r) => String(r.id)).filter(Boolean));
    archivedDays.push(day);
    archivedLogs += dayLogs.length;
  }

  await pruneOldArchives(now);
  return { archivedDays, archivedLogs };
}

/** @param {number} now */
async function pruneOldArchives(now) {
  const keepMs = ANALYTICS_ARCHIVE_KEEP_DAYS * DAY_MS;
  const minDayStart = now - keepMs;
  const list = await idbListArchives();
  for (const row of list) {
    const day = String(row?.day || '');
    if (!day) continue;
    const start = analyticsDayStartMs(day);
    if (start && start < minDayStart) {
      await idbDeleteArchive(day);
    }
  }
}

/**
 * 归档目录摘要（不含 blob，便于 snapshot / 导出说明）
 * @returns {Promise<Array<{ day: string, byteLength: number, logCount: number, createdAt: number }>>}
 */
export async function listAnalyticsArchiveSummaries() {
  const rows = await idbListArchives();
  return rows
    .map((r) => ({
      day: String(r.day || ''),
      byteLength: Number(r.byteLength) || 0,
      logCount: Number(r.logCount) || 0,
      createdAt: Number(r.createdAt) || 0,
    }))
    .filter((r) => r.day)
    .sort((a, b) => a.day.localeCompare(b.day));
}
